// catalog-list.ts — the orchestrator for a [data-ft-catalog-list] mount.
//
// Fetches the FULL card dataset ONCE (id/name/media/source + baked-in numeric attrs like ancestry
// essences) and keeps it in memory. ALL filtering — keyword, source, and essence min/max — then
// runs CLIENT-SIDE against that dataset: instant, zero DB hits after the first load. The server
// payload is itself Redis-cached, so even the first load is a cheap key read. Vanilla DOM + fetch,
// no jQuery.

import type { CatalogCard, CatalogCardGroup, CatalogListConfig, CatalogResponse } from './types';
import { renderCatalogCard } from './render-card';
import { buildIncompleteNotice } from './dom-helpers';
import { mountCatalogTable } from './render-table';
import { mountCatalogRules } from './render-rules';
import { mountCatalogGames } from './render-games';
import { mountCatalogAdventures } from './render-adventures';
import { mountCatalogLibrary } from './render-library';
import { mountCatalogCosmetics } from './render-cosmetics';
import { mountCatalogMyCampaigns } from './render-my-campaigns';
import { mountCatalogMyCampaignPlayers } from './render-my-campaign-players';
import { mountCampaignSessionPlayers } from './render-campaign-session-players';
import { mountCatalogMyLocations } from './render-my-locations';
import { mountCatalogMyWorlds, mountCatalogWorlds } from './render-my-worlds';
import { mountCatalogMyAdventures } from './render-my-adventures';
import { mountCatalogMySources } from './render-my-sources';
import { mountCatalogMyWishlist } from './render-my-wishlist';
import { mountUserProfile } from './render-user-profile';
import { mountCatalogWorldTimeline } from './render-world-timeline';
import { mountCatalogKinFamily } from './render-kin-family';
import { mountCatalogFamilyTree } from './render-family-tree';
import { mountCatalogArticlesTree } from './render-articles-tree';
import { mountCatalogWorldBook } from './render-world-book';
import { mountCatalogWorldBookRail } from './render-world-book-rail';
import { wireFilterClearButton } from './filter-clear';
import { nameGeneratorTables, nameGeneratorSourceIds, type NameGender } from '../janus-engine/advancement/name-generator-22';
import { generateCreatureNames } from '../janus-engine/advancement/random-creature-name';
import './private-item-publish';

function elementValue(id: string): string {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    return el?.value ?? '';
}

function setElementValue(id: string, value: string): void {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    if (!el) return;
    el.value = value;
    el.dispatchEvent(new Event('change', { bubbles: true }));
}

function setFeedbackLoading(targetId: string): HTMLElement | null {
    const target = document.getElementById(targetId);
    const loadingHtml = (window as unknown as { loader?: string }).loader;
    if (target && typeof loadingHtml === 'string') target.innerHTML = loadingHtml;
    return target;
}

function setRequestFailed(target: HTMLElement | null): void {
    if (target) target.innerHTML = '<h5 class="bg-danger text-white rounded p-2 my-2">Request failed</h5>';
}

async function submitNameGenerator(): Promise<void> {
    const target = document.getElementById('div_refresh_names');
    if (!target || target.getAttribute('aria-busy') === 'true') return;
    setFeedbackLoading('div_refresh_names');
    target.setAttribute('aria-busy', 'true');
    try {
        const gender = elementValue('name_gender');
        const name_gender: NameGender = gender === 'male' || gender === 'female' ? gender : 'all';
        const tables = nameGeneratorTables({ name_gender, name_type: elementValue('name_type') });
        const query = new URLSearchParams();
        for (const id of nameGeneratorSourceIds(tables)) query.append('table_ids[]', id);
        const response = await fetch(`${resolveUrl('options/api/name_generator_inputs_inc.php')}?${query}`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('The name tables could not be loaded.');
        const inputs = await response.json() as { rows: Record<string, unknown>[] };
        const names = generateCreatureNames(tables, inputs.rows);
        const list = document.createElement('ul');
        list.className = 'list-group';
        for (const name of names) {
            const row = document.createElement('li');
            row.className = 'list-group-item ft-copy-name';
            row.textContent = name;
            row.setAttribute('role', 'button');
            row.tabIndex = 0;
            row.title = 'Click to copy';
            let copyTimer: ReturnType<typeof setTimeout> | undefined;
            row.addEventListener('click', () => {
                (window as unknown as { copyToClipboard: (element: Element) => void }).copyToClipboard(row);
                row.classList.add('ft-copied');
                clearTimeout(copyTimer);
                copyTimer = setTimeout(() => row.classList.remove('ft-copied'), 1100);
            });
            row.addEventListener('keydown', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    row.click();
                }
            });
            list.appendChild(row);
        }
        target.replaceChildren(list);
    } catch (error) {
        setRequestFailed(target);
        console.error(error);
    } finally {
        target.removeAttribute('aria-busy');
    }
}

function randomTableSaveRange(rangeI: string): void {
    if (!rangeI) return;
    if (typeof openSideLog === 'function') openSideLog();
    void queryDataInputs(resolveUrl('options/tools/random_tables/edit_random_table_inc.php'), 'div_sidelog_warning', {
        item_id: 'item_id',
        random_table_range_i: `random_table_range_i_${rangeI}`,
        roll_range_begin: `roll_range_begin_${rangeI}`,
        roll_range_end: `roll_range_end_${rangeI}`,
        roll_result_effect: `roll_result_effect_${rangeI}`,
        roll_result_effect_description: `roll_result_effect_description_${rangeI}`,
        roll_dice: `roll_dice_${rangeI}`,
        roll_die: `roll_die_${rangeI}`,
        roll_mod: `roll_mod_${rangeI}`,
        item_id_attached: `item_id_attached_${rangeI}`,
    });
}

async function randomTableDeleteRow(rangeI: string): Promise<void> {
    if (!rangeI || !window.confirm('Delete this row permanently? This cannot be undone.')) return;
    if (typeof openSideLog === 'function') openSideLog();
    const refreshEl = setFeedbackLoading('div_sidelog_warning');
    try {
        const response = await fetch(resolveUrl('options/tools/random_tables/delete_random_table_range_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                item_id: elementValue('item_id'),
                random_table_range_i: String(rangeI),
            }),
        });
        const result = await response.text();
        if (refreshEl) refreshEl.innerHTML = result;
        const row = document.getElementById(`random_table_row_${rangeI}`);
        if (row) row.style.display = 'none';
        const loadPost = (window as unknown as { loadPostFunctions?: (force?: boolean) => Promise<void> }).loadPostFunctions;
        if (typeof loadPost === 'function') await loadPost(true);
    } catch (e) {
        console.error(e);
        setRequestFailed(refreshEl);
    }
}

async function randomTableClearAllAttached(): Promise<void> {
    if (!window.confirm('Clear the attached item on every row of this table?')) return;
    if (typeof openSideLog === 'function') openSideLog();
    const refreshEl = setFeedbackLoading('div_sidelog_warning');
    try {
        const response = await fetch(resolveUrl('options/tools/random_tables/clear_all_random_table_attached_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_id: elementValue('item_id') }),
        });
        const result = await response.text();
        if (refreshEl) refreshEl.innerHTML = result;
        const setTomSelectValue = (window as unknown as { setTomSelectValue?: (sel: Element, value: string, opts?: Record<string, unknown>) => void }).setTomSelectValue;
        document.querySelectorAll('select[id^="item_id_attached_"]').forEach((sel) => {
            if (typeof setTomSelectValue === 'function') setTomSelectValue(sel, '', { silent: true });
        });
        const loadPost = (window as unknown as { loadPostFunctions?: (force?: boolean) => Promise<void> }).loadPostFunctions;
        if (typeof loadPost === 'function') await loadPost(true);
    } catch (e) {
        console.error(e);
        setRequestFailed(refreshEl);
    }
}

async function randomTableCleanupRollEffectNames(): Promise<void> {
    if (
        !window.confirm(
            'Run name cleanup on roll_result_effect for every row? Stuck-together words (e.g. OfTheSeverance, WalkedAway) will be split and title-cased. This overwrites existing HTML in that field. Continue?',
        )
    ) {
        return;
    }
    if (typeof openSideLog === 'function') openSideLog();
    const refreshEl = setFeedbackLoading('div_sidelog_warning');
    try {
        const response = await fetch(resolveUrl('options/tools/random_tables/cleanup_random_table_roll_effect_names_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_id: elementValue('item_id') }),
        });
        const result = await response.text();
        if (refreshEl) refreshEl.innerHTML = result;
        const rowMatch = result.match(/random_table_effect_cleanup_rows:(\d+)/);
        if (rowMatch && parseInt(rowMatch[1] ?? '', 10) > 0) {
            window.setTimeout(() => { window.location.reload(); }, 650);
        }
    } catch (e) {
        console.error(e);
        setRequestFailed(refreshEl);
    }
}

function resetRandomTableCreateRollFields(advanceRange: boolean): void {
    const begin = parseFloat(elementValue('roll_range_begin') || '1');
    const end = parseFloat(elementValue('roll_range_end') || '1');
    if (advanceRange) {
        const nextBegin = end + 1;
        const nextEnd = (end - begin) + 1 + end;
        setElementValue('roll_range_begin', String(nextBegin));
        setElementValue('roll_range_end', String(nextEnd));
    } else {
        setElementValue('roll_range_begin', '1');
        setElementValue('roll_range_end', '1');
    }
    setElementValue('roll_result_effect', '');
    setElementValue('roll_result_effect_description', '');
    setElementValue('roll_dice', '0');
    setElementValue('roll_die', '0');
    setElementValue('roll_mod', '0');
    setElementValue('item_id_attached', '');
}

function wireRandomTableEditor(): void {
    const api = window as unknown as {
        randomTableRowSave?: (rangeI: string) => void;
        randomTableQuillAfterEdit?: (evt: Event) => void;
        randomTableDeleteRow?: (rangeI: string) => void;
        randomTableClearAllAttached?: () => void;
        randomTableCleanupRollEffectNames?: () => void;
    };
    api.randomTableRowSave = randomTableSaveRange;
    api.randomTableQuillAfterEdit = (evt: Event) => {
        const el = evt.target as HTMLElement | null;
        const rangeI = el?.dataset?.randomTableRangeI || '';
        randomTableSaveRange(rangeI);
    };
    api.randomTableDeleteRow = (rangeI: string) => { void randomTableDeleteRow(rangeI); };
    api.randomTableClearAllAttached = () => { void randomTableClearAllAttached(); };
    api.randomTableCleanupRollEffectNames = () => { void randomTableCleanupRollEffectNames(); };

    document
        .querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-ft-random-table-range-i]:not([data-ft-random-table-save-mounted="1"])')
        .forEach((el) => {
            el.dataset.ftRandomTableSaveMounted = '1';
            el.addEventListener('change', () => randomTableSaveRange(el.dataset.ftRandomTableRangeI || ''));
        });

    document
        .querySelectorAll<HTMLSelectElement>('select[id^="item_id_attached_"]:not([data-ft-random-table-save-mounted="1"])')
        .forEach((el) => {
            const rangeI = el.id.replace(/^item_id_attached_/, '');
            el.dataset.ftRandomTableSaveMounted = '1';
            el.addEventListener('change', () => randomTableSaveRange(rangeI));
        });
}

function tableColumnPayload(): Record<string, string> {
    return {
        item_id: 'item_id',
        column_1_name: 'column_1_name',
        column_2_name: 'column_2_name',
        column_3_name: 'column_3_name',
        column_4_name: 'column_4_name',
        column_5_name: 'column_5_name',
        column_6_name: 'column_6_name',
        column_7_name: 'column_7_name',
        column_8_name: 'column_8_name',
        column_9_name: 'column_9_name',
        column_10_name: 'column_10_name',
    };
}

function tableColumnRowAddPayload(): Record<string, string> {
    return {
        item_id: 'item_id',
        column_order: 'column_order',
        column_1: 'column_1',
        column_2: 'column_2',
        column_3: 'column_3',
        column_4: 'column_4',
        column_5: 'column_5',
        column_6: 'column_6',
        column_7: 'column_7',
        column_8: 'column_8',
        column_9: 'column_9',
        column_10: 'column_10',
    };
}

function resetTableColumnRowAddFields(): void {
    const order = parseFloat(elementValue('column_order') || '0');
    if (Number.isFinite(order)) setElementValue('column_order', String(order + 1));
    for (let i = 1; i <= 10; i++) setElementValue(`column_${i}`, '');
}

async function tableColumnRowSave(rowI: string): Promise<void> {
    if (!rowI) return;
    const payload: Record<string, string> = {
        item_id: elementValue('item_id'),
        table_column_row_i: rowI,
        column_order: elementValue(`tcr_${rowI}_order`),
    };
    for (let x = 1; x <= 10; x++) payload[`column_${x}`] = elementValue(`tcr_${rowI}_col_${x}`);
    const refreshEl = document.getElementById('div_row_editor_response');
    try {
        const response = await fetch(resolveUrl('options/tools/table_column_rows/update_table_column_row_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (refreshEl) refreshEl.innerHTML = await response.text();
    } catch (e) {
        console.error(e);
        setRequestFailed(refreshEl);
    }
}

async function tableColumnRowDelete(rowI: string): Promise<void> {
    if (!rowI || !window.confirm('Delete this row permanently? This cannot be undone.')) return;
    const refreshEl = document.getElementById('div_row_editor_response');
    try {
        const response = await fetch(resolveUrl('options/tools/table_column_rows/delete_table_column_row_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                item_id: elementValue('item_id'),
                table_column_row_i: rowI,
            }),
        });
        if (refreshEl) refreshEl.innerHTML = await response.text();
        const row = document.getElementById(`tcr_row_${rowI}`);
        if (row) row.style.display = 'none';
    } catch (e) {
        console.error(e);
        setRequestFailed(refreshEl);
    }
}

function wireTableColumnRows(): void {
    const api = window as unknown as {
        tableRowSave?: (rowI: string) => void;
        tableRowDelete?: (rowI: string) => void;
    };
    api.tableRowSave = (rowI: string) => { void tableColumnRowSave(String(rowI)); };
    api.tableRowDelete = (rowI: string) => { void tableColumnRowDelete(String(rowI)); };

    document
        .querySelectorAll<HTMLInputElement>('[data-ft-table-column-row-i]:not([data-ft-table-column-row-mounted="1"])')
        .forEach((input) => {
            input.dataset.ftTableColumnRowMounted = '1';
            input.addEventListener('change', () => { void tableColumnRowSave(input.dataset.ftTableColumnRowI || ''); });
        });
}

function clearOptionalField(id: string): void {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.value = '';
}

function wireUploadMediaPages(): void {
    if (!document.getElementById('input_hidden_header_prompt')) return;
    clearOptionalField('campaign_id');
    clearOptionalField('encounter_id');
}

function wireCatalogPageActions(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-ft-action]:not([data-ft-catalog-action-mounted="1"])').forEach((button) => {
        button.dataset.ftCatalogActionMounted = '1';
        button.addEventListener('click', () => {
            const action = button.dataset.ftAction || '';
            if (action === 'confirm-next') {
                const target = document.getElementById(button.dataset.ftTarget || '');
                button.style.display = 'none';
                if (target) {
                    target.classList.remove('d-none');
                    target.style.display = '';
                }
                return;
            }
            if (action === 'slide-toggle-el') {
                const targetId = button.dataset.ftTarget || '';
                if (typeof window.slideToggleEl === 'function') {
                    window.slideToggleEl(targetId);
                } else {
                    const target = document.getElementById(targetId);
                    if (target) target.style.display = target.style.display === 'none' ? '' : 'none';
                }
                return;
            }
            if (action === 'toggle-el') {
                const fn = (window as unknown as { toggleEl?: (id: string) => void }).toggleEl;
                const targetId = button.dataset.ftTarget || '';
                if (typeof fn === 'function') {
                    fn(targetId);
                } else {
                    const target = document.getElementById(targetId);
                    if (target) target.style.display = target.style.display === 'none' ? '' : 'none';
                }
                return;
            }
            if (action === 'delete-all-encounters') {
                void (async () => {
                    if (typeof openSideLog === 'function') openSideLog();
                    const adventureId = button.dataset.ftAdventureId || '';
                    const campaignId = button.dataset.ftCampaignId || '';
                    await queryData(
                        resolveUrl(adventureId ? 'options/tools/encounters/delete/delete_all_adventure_encounters.php' : 'options/tools/encounters/delete/delete_all_campaign_encounters.php'),
                        'div_sidelog_warning',
                        adventureId ? { adventure_id: adventureId } : { campaign_id: campaignId },
                    );
                    if (adventureId) location.reload();
                })();
                return;
            }
            if (action === 'copy-adventure') {
                const fn = (window as unknown as { copyAdventureToMasterParentID?: (adventureId: string, inputId: string) => void }).copyAdventureToMasterParentID;
                if (typeof fn === 'function') fn(button.dataset.ftAdventureId || '', button.dataset.ftInputId || '');
                return;
            }
            if (action === 'copy-selected-locations') {
                const fn = (window as unknown as { copySelectedLocationsToMasterParentID?: (inputId: string) => void }).copySelectedLocationsToMasterParentID;
                if (typeof fn === 'function') fn(button.dataset.ftInputId || '');
                return;
            }
            if (action === 'move-selected-to-adventure') {
                const fn = (window as unknown as { moveSelectedToAdventure?: (adventureId: string, inputId: string, kind: string, containerId: string) => void }).moveSelectedToAdventure;
                if (typeof fn === 'function') {
                    fn(
                        button.dataset.ftAdventureId || '',
                        button.dataset.ftInputId || '',
                        button.dataset.ftKind || 'locations',
                        button.dataset.ftContainerId || '',
                    );
                }
                return;
            }
            if (action === 'delete-all-locations') {
                const fn = (window as unknown as { deleteAllLocationsExceptCampsiteAndStronghold?: (campaignId: string) => void }).deleteAllLocationsExceptCampsiteAndStronghold;
                if (typeof fn === 'function') fn(button.dataset.ftCampaignId || '');
                return;
            }
            if (action === 'restore-encounters') {
                if (typeof forceOpenSideLog === 'function') forceOpenSideLog();
                const tab = document.getElementById('pills_sidelog_form_tab') as HTMLElement | null;
                tab?.click();
                void queryData(resolveUrl('options/tools/encounters/forms/form_restore_encounters.php'), 'div_refresh_sidelog_form', {
                    encounter_master_parent_id: button.dataset.ftMasterParentId || '',
                });
                return;
            }
            if (action === 'toggle-admins') {
                void queryData(resolveUrl('options/tools/users/toggle_admins_inc.php'), 'div_admin_list', {
                    toggle_admins: button.dataset.ftToggleAdmins || '0',
                });
                return;
            }
            if (action === 'toggle-admin') {
                void queryData(resolveUrl('options/tools/users/toggle_admin_inc.php'), 'div_admin_list', {
                    toggle_admins: button.dataset.ftToggleAdmins || '0',
                    admin_email: button.dataset.ftAdminEmail || '',
                });
                return;
            }
            if (action === 'delete-user') {
                void queryDataInputs(resolveUrl('options/tools/users/delete_user.php'), 'div_refresh', {
                    email: 'email',
                });
                return;
            }
            if (action === 'combat-descriptor-generate') {
                void queryData(resolveUrl('options/tools/combat_descriptor_inc.php'), 'div_refresh', {});
                return;
            }
            if (action === 'name-generator-submit') {
                void submitNameGenerator();
                return;
            }
            if (action === 'attach-layer-to-item') {
                void queryDataInputs(resolveUrl('options/layers/attach_layer_to_item_inc.php'), 'div_refresh', {
                    item_id: 'item_id',
                    item_layer_type_id: 'item_layer_type_id',
                    layer_name: 'layer_name',
                    layer_value: 'layer_value',
                    qty: 'qty',
                });
                return;
            }
            if (action === 'item-type-submit') {
                void queryDataInputs(resolveUrl('options/tools/item_types/modify_item_type.php'), 'div_refresh_modify_item_type', {
                    item_type_id: 'item_type_id',
                    item_type: 'item_type',
                    item_type_name: 'item_type_name',
                });
                return;
            }
            if (action === 'item-submit') {
                const fn = (window as unknown as { editItem?: (post: string) => void }).editItem;
                const endpoint = button.dataset.ftEndpoint || '';
                if (typeof fn === 'function' && endpoint) fn(resolveUrl(endpoint));
                return;
            }
            if (action === 'monster-template-submit') {
                const fn = (window as unknown as { createMonsterTemplate?: (post: string) => void }).createMonsterTemplate;
                const endpoint = button.dataset.ftEndpoint || '';
                if (typeof fn === 'function' && endpoint) fn(resolveUrl(endpoint));
                return;
            }
            if (action === 'table-column-submit') {
                void queryDataInputs(resolveUrl('options/tools/table_columns/modify_table_column_inc.php'), 'div_refresh', tableColumnPayload());
                return;
            }
            if (action === 'table-column-row-add') {
                void queryDataInputs(resolveUrl('options/tools/table_column_rows/modify_table_column_row_inc.php'), 'div_refresh', tableColumnRowAddPayload());
                resetTableColumnRowAddFields();
                return;
            }
            if (action === 'table-column-row-delete') {
                void tableColumnRowDelete(button.dataset.ftRowI || '');
                return;
            }
            if (action === 'random-table-cleanup-effect-names') {
                void randomTableCleanupRollEffectNames();
                return;
            }
            if (action === 'random-table-clear-attached') {
                void randomTableClearAllAttached();
                return;
            }
            if (action === 'random-table-delete-row') {
                void randomTableDeleteRow(button.dataset.ftRangeI || '');
                return;
            }
            if (action === 'random-table-create-roll') {
                void queryDataInputs(resolveUrl('options/tools/random_tables/create_random_roll_inc.php'), 'div_refresh', {
                    item_id: 'item_id',
                    roll_range_begin: 'roll_range_begin',
                    roll_range_end: 'roll_range_end',
                    roll_result_effect: 'roll_result_effect',
                    roll_result_effect_description: 'roll_result_effect_description',
                    select_bulk_import: 'select_bulk_import',
                    roll_dice: 'roll_dice',
                    roll_die: 'roll_die',
                    roll_mod: 'roll_mod',
                    item_id_attached: 'item_id_attached',
                });
                resetRandomTableCreateRollFields(true);
                return;
            }
            if (action === 'random-table-reset-create-roll') {
                resetRandomTableCreateRollFields(false);
                return;
            }
            if (action === 'random-table-roll') {
                if (typeof openSideLog === 'function') openSideLog();
                // Lane N2 RT-3 (2026-10-01): the browser rolls the table (websocket/roll-truth.ts → roll_truth_commit_inc.php kind
                // `table`); the GM roller endpoint is gone. No campaign context, like the old tool (the roll lands on the user's log).
                const tableId = (document.getElementById('item_random_table') as HTMLSelectElement | null)?.value ?? '';
                const roll = (window as unknown as { ftTryCommitTableRoll?: (id: string, ctx: Record<string, unknown>) => Promise<unknown> }).ftTryCommitTableRoll;
                if (typeof roll === 'function' && tableId) void roll(tableId, {});
                else if (typeof displayWarning === 'function') displayWarning('Pick a table to roll.');
                return;
            }
            if (action === 'video-primary') {
                void queryData(resolveUrl('options/tools/videos/update/update_primary_video_inc.php'), 'div_response_videos', {
                    video_id: button.dataset.ftVideoId || '',
                });
                document.querySelectorAll<HTMLElement>('.primary_video').forEach((btn) => {
                    btn.classList.remove('btn-info');
                    btn.classList.add('btn-primary');
                });
                button.classList.remove('btn-primary');
                button.classList.add('btn-info');
                return;
            }
            if (action === 'video-private') {
                const videoId = button.dataset.ftVideoId || '';
                const makePrivate = button.dataset.ftVideoPrivate || '0';
                void (async () => {
                    await queryData(resolveUrl('options/tools/videos/update_video_private_inc.php'), 'div_response_videos', {
                        item_type: 'video',
                        video_id: videoId,
                        video_private: makePrivate,
                    });
                    const publicBtn = document.getElementById(`video_public_${videoId}`);
                    const privateBtn = document.getElementById(`video_private_${videoId}`);
                    if (publicBtn) publicBtn.style.display = makePrivate === '1' ? 'none' : 'block';
                    if (privateBtn) privateBtn.style.display = makePrivate === '1' ? 'block' : 'none';
                })();
                return;
            }
            if (action === 'video-delete') {
                const videoId = button.dataset.ftVideoId || '';
                const hard = button.dataset.ftHardDelete === '1';
                void queryData(
                    resolveUrl(hard ? 'options/tools/videos/delete/delete_video_inc.php' : 'options/db_queries/delete/soft_delete_inc.php'),
                    hard ? 'div_sidelog_warning' : 'div_response_videos',
                    hard ? { video_id: videoId } : { item_type: 'video', video_id: videoId },
                );
                const wrap = document.getElementById(`div_video_${videoId}`);
                if (wrap) wrap.style.display = 'none';
                return;
            }
            if (action === 'video-update') {
                void queryDataInputs(resolveUrl('options/tools/videos/modify_video_inc.php'), 'div_response_videos', {
                    video_id: 'video_id',
                    video_name: 'video_name',
                    video_description: 'video_description',
                });
                return;
            }
            if (action === 'copy-prompt') {
                const fn = (window as unknown as { copyInputToClipboard?: (...ids: string[]) => void }).copyInputToClipboard;
                const ids = (button.dataset.ftInputIds || '').split(',').map((id) => id.trim()).filter(Boolean);
                if (typeof fn === 'function' && ids.length) fn(...ids);
                return;
            }
            if (action === 'picture-primary') {
                void queryData(resolveUrl('options/tools/pictures/update/update_primary_picture_inc.php'), 'div_sidelog_warning', {
                    pic_id: button.dataset.ftPicId || '',
                });
                document.querySelectorAll<HTMLElement>('.primary_picture').forEach((btn) => {
                    btn.classList.remove('btn-info');
                    btn.classList.add('btn-primary');
                });
                button.classList.remove('btn-primary');
                button.classList.add('btn-info');
                return;
            }
            if (action === 'picture-update') {
                void queryDataInputs(resolveUrl('options/tools/pictures/modify_picture_inc.php'), 'div_sidelog_warning', {
                    pic_id: 'pic_id',
                    picture_name: 'picture_name',
                    picture_description: 'picture_description',
                });
            }
        });
    });
}

function parseJsonAttr(raw: string | null): Record<string, string> {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch {
        return {};
    }
}

function parseConfig(root: HTMLElement): CatalogListConfig {
    return {
        endpoint: root.getAttribute('data-ft-endpoint') || '',
        renderMode: (() => {
            const m = root.getAttribute('data-ft-render');
            return m === 'table' ? 'table' : m === 'rules' ? 'rules' : m === 'games' ? 'games' : m === 'adventures' ? 'adventures' : m === 'library' ? 'library' : m === 'cosmetics' ? 'cosmetics' : m === 'my-campaigns' ? 'my-campaigns' : m === 'my-campaign-players' ? 'my-campaign-players' : m === 'campaign-session-players' ? 'campaign-session-players' : m === 'my-locations' ? 'my-locations' : m === 'my-worlds' ? 'my-worlds' : m === 'worlds' ? 'worlds' : m === 'my-adventures' ? 'my-adventures' : m === 'my-sources' ? 'my-sources' : m === 'my-wishlist' ? 'my-wishlist' : m === 'user-profile' ? 'user-profile' : m === 'world-book' ? 'world-book' : m === 'world-book-rail' ? 'world-book-rail' : m === 'world-timeline' ? 'world-timeline' : m === 'kin-family' ? 'kin-family' : m === 'family-tree' ? 'family-tree' : m === 'articles-tree' ? 'articles-tree' : 'cards';
        })(),
        searchInputId: root.getAttribute('data-ft-search-input-id'),
        filterButtonId: root.getAttribute('data-ft-filter-button-id'),
        filterClearButtonId: root.getAttribute('data-ft-filter-clear-button-id'),
        filterPanelId: root.getAttribute('data-ft-filter-panel-id'),
        groupNavId: root.getAttribute('data-ft-group-nav-id'),
        // Empty (or absent) label → no section header for that group, so a catalog with no
        // Standard/Advanced split renders as one clean ungrouped grid (e.g. lineages.php).
        sectionLabels: {
            standard: root.getAttribute('data-ft-section-standard') || '',
            advanced: root.getAttribute('data-ft-section-advanced') || '',
        },
        filterFields: parseJsonAttr(root.getAttribute('data-ft-filter-fields')),
        loadingLogo: root.getAttribute('data-ft-loading-logo'),
        loadingText: root.getAttribute('data-ft-loading-text'),
        tableCap: (() => {
            const n = parseInt(root.getAttribute('data-ft-table-cap') || '', 10);
            return Number.isFinite(n) && n > 0 ? n : null;
        })(),
    };
}

// Read a form control's current value by element id. Handles <select multiple>, <select>,
// and <input> — returns string[] for multi-selects so the PHP side sees an array.
function readFieldValue(elementId: string): string | string[] {
    const el = document.getElementById(elementId) as HTMLInputElement | HTMLSelectElement | null;
    if (!el) return '';
    if (el instanceof HTMLSelectElement && el.multiple) {
        return Array.from(el.selectedOptions).map((o) => o.value);
    }
    return el.value ?? '';
}

// Exported: render-table.ts reuses the same endpoint resolution.
export function resolveUrl(endpoint: string): string {
    if (/^https?:\/\//i.test(endpoint) || endpoint.startsWith('/')) return endpoint;
    return window.location.origin + '/' + endpoint.replace(/^\.?\//, '');
}

// All filtering is client-side now, so the dataset is fetched filter-agnostic (full set). Kept as a
// helper in case a future catalog needs to scope the server payload (e.g. per-user entitlements).
function buildRequestBody(): Record<string, unknown> {
    return {};
}

function asArray(v: string | string[]): string[] {
    return Array.isArray(v) ? v.filter(Boolean) : (v ? [v] : []);
}

// Client-side filter predicate for one card, reading the live filter-panel controls named by the
// mount's data-ft-filter-fields map. Ancestry-specific roles ('source', 'ancestral_essence_*') are
// applied when their controls exist; a catalog page without them simply skips that check.
function matchesFilters(card: CatalogCard, config: CatalogListConfig): boolean {
    const ff = config.filterFields;

    // Source (multi-select): when any source is chosen, the card's source must be one of them.
    if (ff.source) {
        const selected = asArray(readFieldValue(ff.source));
        if (selected.length && !selected.includes(card.source?.source_id ?? '')) {
            return false;
        }
    }

    // Required class (multi-select, subclasses): when any class is chosen, the card's required
    // class must be one of them. Cards with no required class are excluded once the filter is set.
    if (ff.requires_class) {
        const selected = asArray(readFieldValue(ff.requires_class));
        if (selected.length && !selected.includes(card.requires_class ?? '')) {
            return false;
        }
    }

    // Essence type + min/max range. Selecting a type filters to cards that HAVE that essence
    // (non-null) and fall within the optional bounds — mirrors the old server-side filter.
    if (ff.ancestral_essence_type) {
        const type = String(readFieldValue(ff.ancestral_essence_type) || '').trim();
        if (type) {
            const v = card.essences ? card.essences[type] : undefined;
            if (v === null || v === undefined) return false;
            const minRaw = ff.ancestral_essence_value_min
                ? String(readFieldValue(ff.ancestral_essence_value_min) || '').trim() : '';
            const maxRaw = ff.ancestral_essence_value_max
                ? String(readFieldValue(ff.ancestral_essence_value_max) || '').trim() : '';
            if (minRaw !== '' && v < parseFloat(minRaw)) return false;
            if (maxRaw !== '' && v > parseFloat(maxRaw)) return false;
        }
    }

    return true;
}

// Exported: render-table.ts mounts its table / loader / status into the same wrapper elements.
export function results(root: HTMLElement): HTMLElement | null {
    return root.querySelector('[data-ft-catalog-results]');
}
export function status(root: HTMLElement): HTMLElement | null {
    return root.querySelector('[data-ft-catalog-status]');
}

// Eagerly create Bootstrap tooltip instances on freshly-injected content (cards/rows built after page
// load). The front-end iconMap() emits `data-bs-toggle="tooltip"` spans carrying the label in
// `data-bs-original-title` but NO `title`. This is actually REDUNDANT with template.js's body-level
// hover delegation: Bootstrap 5.3.8's `_getTitle()` is `config.title || getAttribute('data-bs-
// original-title')`, so `Tooltip.getOrCreateInstance(el).show()` on hover already resolves the label
// and shows instantly — no `title` attribute required. We keep this belt-and-suspenders pass (cheap,
// reuses existing instances) but it is NOT what makes the tooltips work; the delegation is. See
// `.cursor/rules/press-hold-system.mdc` → "Hover/click tooltip mechanism". No-op if Bootstrap absent.
export function initTooltips(container: HTMLElement | null): void {
    if (!container) return;
    // Hydrate any <i class="ft-icon"> placeholders (PHP icon_ph) this render injected BEFORE wiring
    // tooltips — ftMountIcons replaces each placeholder with the iconMap() span carrying
    // data-bs-original-title, so the querySelectorAll below then finds and wires them. Idempotent
    // (already-mounted placeholders are skipped). This is the shared finalize for the table/rules/
    // my-campaigns/my-adventures/my-sources/default-card renderers; games/adventures/library call
    // ftMountIcons explicitly at their own build-end.
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(container); } catch { /* footer pass / delegation covers it */ }
    }
    const bs = (window as unknown as { bootstrap?: { Tooltip?: { getOrCreateInstance(el: Element, cfg?: Record<string, unknown>): unknown } } }).bootstrap;
    if (!bs || !bs.Tooltip) return;
    container.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        const title = el.getAttribute('data-bs-original-title') || el.getAttribute('title') || '';
        if (!title) return;
        try { bs.Tooltip!.getOrCreateInstance(el, { title }); } catch { /* ignore */ }
    });
}

export function setStatus(root: HTMLElement, msg: string): void {
    const el = status(root);
    if (el) {
        el.textContent = msg;
        el.style.display = msg ? '' : 'none';
    }
}

// Branded loading state. Mounts INSIDE the results grid (not the status line below it), so it sits
// centered in the reserved min-height space instead of way down the page. The FateTide mark pulses
// inside a cyan ring; the host page styles it (.ft-catalog-loader*, see ancestries.php).
//
// The ring is a determinate-LOOKING progress arc: a fetch() exposes no real download progress (the
// cached JSON payload arrives in one shot, no Content-Length), so we SIMULATE it — ease toward ~95%
// while the request is in flight, then hideLoader() snaps to 100% and removes it. The fill % is
// driven from JS via the --ft-progress custom property on the ring (the CSS reads it in a
// conic-gradient). The only page input is the optional logo URL + caption (data-ft-* attrs).
export function showLoader(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container || container.querySelector('.ft-catalog-loader')) return;

    const loader = document.createElement('div');
    loader.className = 'ft-catalog-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');

    const mark = document.createElement('div');
    mark.className = 'ft-catalog-loader-mark';
    const ring = document.createElement('span');
    ring.className = 'ft-catalog-loader-ring';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.setProperty('--ft-progress', '0');
    mark.appendChild(ring);
    if (config.loadingLogo) {
        const logo = document.createElement('img');
        logo.className = 'ft-catalog-loader-logo';
        logo.src = config.loadingLogo;
        logo.alt = '';
        logo.setAttribute('aria-hidden', 'true');
        mark.appendChild(logo);
    }
    loader.appendChild(mark);

    const pct = document.createElement('div');
    pct.className = 'ft-catalog-loader-pct';
    pct.textContent = '0%';
    loader.appendChild(pct);

    const caption = document.createElement('div');
    caption.className = 'ft-catalog-loader-text';
    caption.textContent = config.loadingText || 'Loading';
    loader.appendChild(caption);

    container.appendChild(loader);

    // Simulated progress: decelerate toward a 95% ceiling so it never "completes" on its own — the
    // real completion (snap to 100% + remove) is hideLoader's job. Stored on the element so hideLoader
    // can stop it without a module-level handle (multiple mounts can each own a loader).
    let progress = 0;
    const tick = (): void => {
        progress = Math.min(95, progress + Math.max(0.5, (95 - progress) * 0.06));
        ring.style.setProperty('--ft-progress', String(progress));
        pct.textContent = Math.round(progress) + '%';
    };
    tick();
    loader.dataset.ftProgressTimer = String(window.setInterval(tick, 90));
}

// Stop a loader's simulated-progress interval, if any. Safe to call on a loader that never started one
// (e.g. a fast load that cleared before the 250ms timer fired). MUST run on EVERY teardown path so a
// detached loader node doesn't keep an interval ticking forever — including buildList(), which wipes
// the container out from under the loader without going through hideLoader().
function stopLoaderProgress(loader: HTMLElement | null | undefined): void {
    const timer = loader?.dataset.ftProgressTimer;
    if (timer) window.clearInterval(Number(timer));
}

export function hideLoader(root: HTMLElement): void {
    const loader = results(root)?.querySelector('.ft-catalog-loader') as HTMLElement | null;
    if (!loader) return;
    stopLoaderProgress(loader);
    loader.remove();
}

// Section heading ("Standard" / "Advanced"). Emits .ft-catalog-section-title — the host page styles
// it (accent bar + uppercase rule); see the <style> block on ancestries.php. Extra top margin on
// the second+ section separates it from the preceding card grid.
function sectionHeader(label: string, advanced: boolean): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = advanced ? 'col-12 mt-5 mb-3' : 'col-12 mb-3';
    const h2 = document.createElement('h2');
    h2.className = 'ft-catalog-section-title';
    h2.textContent = label;
    wrap.appendChild(h2);
    return wrap;
}

// Accent-bar group header for the N-group form (e.g. subclasses by class): reuses the rules-index
// header shape (.ft-rules-group-title — accent left bar + icon backing + uppercase label, see
// css/style.css), wrapped in a col-12 so it spans the full width of the results .row grid. The icon
// is resolved client-side from the group's `icon_id` via iconMap() (pure-data payload, full JS/TS).
// Stable per-group anchor id, shared by the group header (scroll target) and the jump-bar button
// (scroll source). Keyed off the group's icon_id (a class UUID for subclasses) so it survives re-
// renders; falls back to the index for header-only/iconless groups (e.g. "Other").
function groupAnchorId(group: CatalogCardGroup, index: number): string {
    return 'ftcat-group-' + (group.icon_id || index);
}

function accentGroupHeader(group: CatalogCardGroup, index: number): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'col-12 ft-cat-group-anchor';
    wrap.id = groupAnchorId(group, index);
    const header = document.createElement('div');
    header.className = 'ft-rules-group-title';
    header.style.setProperty('--ft-accent', group.accent || '#22d3ee');
    if (group.icon_id) {
        const icon = document.createElement('span');
        icon.className = 'ft-rules-group-icon';
        icon.innerHTML = iconMap(group.icon_id);
        header.appendChild(icon);
    }
    const label = document.createElement('span');
    label.className = 'ft-rules-group-label';
    label.textContent = group.label;
    header.appendChild(label);
    wrap.appendChild(header);
    return wrap;
}

// Group jump-bar: fill an EXTERNAL container (the page's header, by id via data-ft-group-nav-id) with
// one icon button per rendered group, each smooth-scrolling to that group's anchor header. Only the
// N-group form has groups; pages without it (or without the attribute) get nothing. Built once after
// the cards render — it mirrors the groups exactly, so it can't point at a class section that isn't
// there (e.g. classes with no subclasses are never emitted as a group, so they get no button).
function buildGroupNav(config: CatalogListConfig, data: CatalogResponse): void {
    if (!config.groupNavId) return;
    const nav = document.getElementById(config.groupNavId);
    if (!nav) return;

    nav.innerHTML = '';
    const groups = (data.groups ?? []).filter((g) => g.label); // skip headerless continuation runs
    if (!groups.length) {
        nav.style.display = 'none';
        return;
    }

    (data.groups ?? []).forEach((group, i) => {
        if (!group.label) return;
        const anchor = groupAnchorId(group, i);
        const btn = document.createElement('a');
        btn.href = '#' + anchor;
        btn.className = 'ft-cat-group-jumper';
        btn.setAttribute('aria-label', 'Jump to ' + group.label);
        btn.setAttribute('data-bs-toggle', 'tooltip');
        btn.setAttribute('data-bs-original-title', group.label);
        // Class icon when the group has one (icon_id → iconMap()), else the label text as a chip. The
        // icon is a bare <img> (title=false): iconMap's own tooltip span would stack a second hover label
        // on the button's, and the button's aria-label already names it, so the alt stays empty.
        if (group.icon_id) {
            btn.innerHTML = iconMap(group.icon_id, 30, 30, false);
            btn.querySelector('img')?.setAttribute('alt', '');
        } else {
            btn.textContent = group.label;
        }
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const target = document.getElementById(anchor);
            if (!target) return;
            // A sticky nav pins over the page, and its height varies (wrapped rows, the <xl mobile
            // top bar under it), so land the header just below the nav's real rendered bottom rather
            // than a fixed scroll-margin.
            const navStyle = getComputedStyle(nav);
            if (navStyle.position !== 'sticky') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
            const pinnedBottom = (parseFloat(navStyle.top) || 0) + nav.offsetHeight;
            const y = target.getBoundingClientRect().top + window.scrollY - pinnedBottom - 8;
            window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        });
        nav.appendChild(btn);
    });

    nav.style.display = '';
    initTooltips(nav);
}

interface BuiltCard { el: HTMLElement; card: CatalogCard; }
interface BuiltSection { header: HTMLElement | null; cards: BuiltCard[]; }
interface BuiltList { sections: BuiltSection[]; emptyEl: HTMLElement; }

// Build the full card DOM ONCE into the container. Filtering then toggles each element's
// visibility instead of rebuilding — so search/filter never tears down + recreates nodes
// (no flicker, no image re-fetch). Returns the element refs for the filter pass.
function buildList(root: HTMLElement, config: CatalogListConfig, data: CatalogResponse): BuiltList | null {
    const container = results(root);
    if (!container) return null;
    // Card success path: this wipes the loader node directly (no hideLoader call), so stop its
    // progress interval first or it would tick on forever against a detached element.
    stopLoaderProgress(container.querySelector('.ft-catalog-loader') as HTMLElement | null);
    container.innerHTML = '';

    const sections: BuiltSection[] = [];

    const renderGroup = (cards: CatalogCard[], header: HTMLElement | null): void => {
        if (header) container.appendChild(header);
        const built: BuiltCard[] = cards.map((card) => {
            const el = renderCatalogCard(card);
            container.appendChild(el);
            return { el, card };
        });
        sections.push({ header, cards: built });
    };

    if (data.groups && data.groups.length) {
        // N-group form (e.g. subclasses by class): one accent-headed section per group, in payload
        // order. Filtering/search still toggles each section the same way as the standard/advanced
        // split — a group whose cards are all filtered out hides its header too.
        data.groups.forEach((group, i) => {
            const header = group.label ? accentGroupHeader(group, i) : null;
            renderGroup(group.cards ?? [], header);
        });
    } else {
        // Fixed standard/advanced split (ancestries, lineages, …). No label → no header (single
        // ungrouped catalog); the toolbar title carries the page name.
        const groups: Array<{ cards: CatalogCard[]; label: string; advanced: boolean }> = [
            { cards: data.sections?.standard ?? [], label: config.sectionLabels.standard, advanced: false },
            { cards: data.sections?.advanced ?? [], label: config.sectionLabels.advanced, advanced: true },
        ];
        for (const group of groups) {
            const header = group.label ? sectionHeader(group.label, group.advanced) : null;
            renderGroup(group.cards, header);
        }
    }

    // Empty-state message lives INSIDE the grid so it sits at the TOP of the reserved min-height
    // space (not pushed below it). Hidden until a filter hides everything.
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = 'No results found.';
    emptyEl.style.display = 'none';
    container.appendChild(emptyEl);

    return { sections, emptyEl };
}

// Apply the current keyword + filter-panel state by toggling visibility on the prebuilt elements.
// A section header hides when all its cards are filtered out; the empty message shows when nothing
// matches across both sections.
function applyFilter(config: CatalogListConfig, built: BuiltList, keyword: string): void {
    let totalVisible = 0;
    for (const section of built.sections) {
        let visible = 0;
        for (const { el, card } of section.cards) {
            const show = matchesFilters(card, config)
                && (!keyword || (card.item_name || '').toLowerCase().includes(keyword));
            el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (section.header) section.header.style.display = visible > 0 ? '' : 'none';
        totalVisible += visible;
    }
    built.emptyEl.style.display = totalVisible === 0 ? '' : 'none';
}

function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

export function mountCatalogList(root: HTMLElement): void {
    if (root.getAttribute('data-ft-catalog-mounted') === '1') return;
    root.setAttribute('data-ft-catalog-mounted', '1');

    const config = parseConfig(root);
    if (!config.endpoint) {
        console.error('[catalog-list] mount missing data-ft-endpoint', root);
        return;
    }

    // Table renderer is a sibling on the same JSON pipeline — hand off and we're done.
    if (config.renderMode === 'table') {
        mountCatalogTable(root, config);
        return;
    }

    // Rules-index renderer (rules.php): grouped, accent-headed navigation grid — same pipeline,
    // different shape than the 2-section card catalog.
    if (config.renderMode === 'rules') {
        mountCatalogRules(root, config);
        return;
    }

    // Game-listing renderer (free_games.php / paid_games.php): bespoke campaign-listing cards — same
    // pipeline, search-only filtering.
    if (config.renderMode === 'games') {
        mountCatalogGames(root, config);
        return;
    }

    // Adventure-listing renderer (adventures.php / free_adventures.php / paid_adventures.php): bespoke
    // adventure catalog cards — same pipeline, search-only filtering.
    if (config.renderMode === 'adventures') {
        mountCatalogAdventures(root, config);
        return;
    }

    // Library renderer (library.php / my_library.php): entitlement-grouped source (digital product)
    // cards — same pipeline, with a client-side product-type chip filter + keyword search.
    if (config.renderMode === 'library') {
        mountCatalogLibrary(root, config);
        return;
    }

    // Cosmetics Store renderer (cosmetics.php): purchasable 3D looks as model-family cards (colour skins as
    // swatches), one section per class — keyword search + Type / Class chip filters, all client-side.
    if (config.renderMode === 'cosmetics') {
        mountCatalogCosmetics(root, config);
        return;
    }

    // My-campaigns renderer (my_campaigns.php): the GM dashboard's rich campaign-management cards —
    // same pipeline, search-only filtering, with card shell + management actions rendered client-side.
    if (config.renderMode === 'my-campaigns') {
        mountCatalogMyCampaigns(root, config);
        return;
    }

    // My-campaign-players renderer (tools/campaigns/my_campaign_players.php): the GM's player
    // table with action delegation kept in TypeScript instead of inline onclick handlers.
    if (config.renderMode === 'my-campaign-players') {
        mountCatalogMyCampaignPlayers(root, config);
        return;
    }

    if (config.renderMode === 'campaign-session-players') {
        mountCampaignSessionPlayers(root, config);
        return;
    }

    if (config.renderMode === 'my-locations') {
        mountCatalogMyLocations(root, config);
        return;
    }

    // My-worlds renderer (my_worlds.php): the creator's world-management cards — same pipeline,
    // search-only filtering, with the card shell and management actions rendered client-side from
    // structured endpoint data.
    if (config.renderMode === 'my-worlds') {
        mountCatalogMyWorlds(root, config);
        return;
    }

    // Worlds browse (worlds.php): every public world as a read-only card into its World Book.
    if (config.renderMode === 'worlds') {
        mountCatalogWorlds(root, config);
        return;
    }

    // My-adventures renderer (my_adventures.php): the creator's own adventure-management cards —
    // same pipeline, search-only filtering, with the card shell and management actions rendered
    // client-side from structured endpoint data.
    if (config.renderMode === 'my-adventures') {
        mountCatalogMyAdventures(root, config);
        return;
    }

    // My-sources renderer (my_sources.php): the publisher's own source-management cards — same
    // pipeline, with Public/Private grouping, keyword search, product-type chips, source media, and
    // management actions all rendered client-side from structured endpoint data.
    if (config.renderMode === 'my-sources') {
        mountCatalogMySources(root, config);
        return;
    }

    // My-wishlist renderer (my_wishlist.php): compact saved-source cards with move-to-cart/remove
    // actions rendered client-side from structured endpoint data.
    if (config.renderMode === 'my-wishlist') {
        mountCatalogMyWishlist(root, config);
        return;
    }

    // User-profile renderer (user.php): a SINGLE public profile record, not a list — same pipeline,
    // no search/filter. Paints identity, media carousel, socials, rich text, and stat tiles client-side.
    if (config.renderMode === 'user-profile') {
        mountUserProfile(root, config);
        return;
    }

    // World-timeline renderer (world_timeline.php): a world's history as era bands running down
    // the page with one lane per track — same pipeline, but the payload is a CHRONOLOGY, already
    // ordered server-side by the signed BT/AT sort key. The renderer never re-sorts it.
    if (config.renderMode === 'world-book-rail') {
        mountCatalogWorldBookRail(root, config);
        return;
    }
    if (config.renderMode === 'world-book') {
        mountCatalogWorldBook(root, config);
        return;
    }
    if (config.renderMode === 'world-timeline') {
        mountCatalogWorldTimeline(root, config);
        return;
    }

    // Kin-family renderer: ONE creature's immediate family plus the authoring widget. Mounted on
    // the catalog creature's console AND on a placed creature's GM panel — the same component in
    // both, because the endpoint decides what may be seen and edited, not the mount (owner ruling
    // 2026-08-20). The mount carries only data-ft-kin-kind + data-ft-kin-id.
    if (config.renderMode === 'kin-family') {
        mountCatalogKinFamily(root, config);
        return;
    }

    // Family-tree renderer (family_tree.php): the full diagram as generation bands with SVG edges.
    // Nothing stores the tree — it is derived from parent pointers — so the renderer is handed
    // edges and builds the generations itself.
    if (config.renderMode === 'family-tree') {
        mountCatalogFamilyTree(root, config);
        return;
    }

    // Articles-tree renderer (my_articles.php's Tree view + article.php's left-hand nav): ONE
    // scope's articles as a folder tree. Categories are folders, article_parent_id is the path —
    // the flat table can only ever show the leaves. The mount carries the scope, and on
    // my_articles the picker changes it without a page load (see `ft-articles-tree-reload`).
    if (config.renderMode === 'articles-tree') {
        mountCatalogArticlesTree(root, config);
        return;
    }

    // Cards are built ONCE into `built`; filtering toggles their visibility (no rebuild → no flicker).
    let built: BuiltList | null = null;

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const currentKeyword = () => (searchInput?.value || '').trim().toLowerCase();

    // Re-apply the keyword + filter-panel state to the prebuilt cards. No fetch, no DB, no rebuild.
    const paint = () => {
        if (built) applyFilter(config, built, currentKeyword());
    };

    // Fetch the full dataset once (server payload is Redis-cached, so this is a cheap key read),
    // build the DOM once, then apply the current filter state.
    const fetchBase = async (): Promise<void> => {
        // Only show the loader if the fetch is slow (>250ms) — a fast load goes straight to cards
        // with no flash. buildList() clears the container, which removes the loader on success.
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildRequestBody()),
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const { adoptCharacterDefinitionCards } = await import('./character-definition-cards');
            const data = await adoptCharacterDefinitionCards(await res.json());
            clearTimeout(loadingTimer);
            setStatus(root, '');
            built = buildList(root, config, data);
            const partial = buildIncompleteNotice(data.incomplete);
            if (partial) results(root)?.prepend(partial);
            paint();
            // Init Bootstrap tooltips on freshly-built card icons (classes/subclasses lead with an
            // iconMap() icon whose label is in data-bs-original-title) — see initTooltips().
            initTooltips(results(root));
            // Hover-play header videos (Fable 2026-07-01 — the wiring gap from the video-fade entry):
            // initializeVideoCards() (campaign-init) is a one-shot DOMContentLoaded pass that ran
            // BEFORE this async fetch/render, so classes/subclasses videos only ever showed their
            // poster frame (no intro autoplay, no hover-play). Re-call it after the cards exist —
            // same pattern as render-library / render-my-adventures / render-my-sources. Idempotent
            // (`:not(.initialized)` guard).
            const initVideos = (window as unknown as { initializeVideoCards?: () => void }).initializeVideoCards;
            if (typeof initVideos === 'function') initVideos();
            // Fill the optional header jump-bar (subclasses) with one class-icon button per group.
            buildGroupNav(config, data);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-list] fetch/render failed', e);
            setStatus(root, 'Could not load results. Please try again.');
        }
    };

    // Live keyword search (client-side, instant) + the house spyglass/clear trailing icons. This
    // mount owns the search box outright — the generic filter-search-init.js inline-search
    // delegation is detached (the page drops data-ft-inline-search) so nothing re-POSTs the old way.
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = () => {
            if (!glyph || !clear) return;
            const hasText = currentKeyword().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); paint(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                paint();
            });
        }
        syncTrailing();
    }

    // Filter "apply" button: close the panel, then filter the in-memory dataset (no fetch).
    if (config.filterButtonId) {
        const btn = document.getElementById(config.filterButtonId);
        if (btn) {
            btn.addEventListener('click', () => {
                closePanel(config.filterPanelId);
                paint();
            });
        }
    }

    // Footer "Clear all": reset the panel's controls + re-filter (panel stays open). Shared helper.
    wireFilterClearButton(config, paint);

    // Initial dataset load.
    void fetchBase();
}

export function mountCatalogLists(): void {
    wireRandomTableEditor();
    wireTableColumnRows();
    wireUploadMediaPages();
    wireCatalogPageActions();
    document
        .querySelectorAll<HTMLElement>('[data-ft-catalog-list]:not([data-ft-catalog-mounted="1"])')
        .forEach(mountCatalogList);
}
